/* eslint-disable no-undef */
if (!customElements.get('product-media')) {
  class ProductMedia extends HTMLElement {
    constructor () {
      super()
      this.thumbnailClickListeners = []
      this.navigationClickListeners = {}
      this.videoTimeouts = {}
      this.hintTimeouts = {}
      this.resizeTimeout = null
    }

    connectedCallback () {
      if (this.dataset.productMediaSize === '1') return
      if (EmblaCarousel) {
        this.firstTimeLoad = localStorage.getItem(`firstTimeLoad-${this.dataset.productId}`) === null
        this.loading = true
        this.lastViewport = window.innerWidth >= 1280 ? 'desktop' : 'mobile'
        this.initCarousel().then(() => {
          this.loading = false
          this.setupOverviewButtonSizing()
        })
      }
      const variantImages = this.variantImages[String(this.initialVariantId)] || []
      if (variantImages.length > 0) {
        this.initialLoadWithAdditionalImages(variantImages)
      }
      this.addEventListener('variantChange', this.onVariantChange.bind(this))
      this.handleResize = this.handleResize.bind(this)
      window.addEventListener('resize', this.handleResize)
    }

    async initCarousel () {
      this.mainOptions = {
        speed: 1,
        duration: 1,
        skipSnaps: false,
        dragThreshold: 1,
        containScroll: 'trimSnaps',
        loop: true,
        startIndex: this.getInitialMainIndex(),
        watchDrag: (emblaApi, event) => {
          const progressBarDragging = event.target.closest('product-gallery-video[data-progress-dragging="true"]')
          return !progressBarDragging
        },
      }
      this.thumbOptions = {
        dragFree: true,
        loop: false,
        align: 'center',
        speed: 1,
        duration: 20,
        startIndex: this.getInitialThumbIndex(),
        breakpoints: {
          '(min-width: 1280px)': {
            active: false,
          },
        },
      }
      await this.setupCarousels().then(() => {
        this.setupThumbnailClick()
        if (this.firstTimeLoad) {
          localStorage.setItem(`firstTimeLoad-${this.dataset.productId}`, 'false')
          this.handleFirstTimeLoad()
        }
      })
      if (!document.getElementById('fullscreen-hint-styles')) {
        const style = document.createElement('style')
        style.id = 'fullscreen-hint-styles'
        style.innerHTML = `
          .fullscreen-hint{position:absolute;bottom:calc(100% + 10px);right:0;white-space:nowrap;z-index:40}
          .fullscreen-hint::after{content:'';position:absolute;top:100%;right:14px;border:6px solid transparent;border-top-color:#ffffff}
        `
        document.head.appendChild(style)
      }
    }

    async initialLoadWithAdditionalImages (images) {
      const variantId = String(this.initialVariantId)
      this.featuredMedia.forEach((featuredMedia) => {
        featuredMedia.classList.toggle('tb-hidden', featuredMedia.dataset.variantId !== variantId)
      })
      this.removeAdditionalImages()
      await this.addAdditionalImages(variantId, images).then(() => {
        this.reinitializeCarousel()
      })
    }

    async setupCarousels () {
      this.mainEmbla = EmblaCarousel(this.querySelector('.product-carousel__viewport'), this.mainOptions)
      let thumbViewport = null
      const isMobile = window.innerWidth < 1280
      if (this.hasLongformVideo && isMobile) {
        thumbViewport = this.querySelector('.product-carousel-thumbs__viewport-mobile')
      }
      if (!thumbViewport) {
        thumbViewport = this.querySelector('.product-carousel-thumbs__viewport')
      }
      if (!thumbViewport) {
        console.error('Could not find thumbnail carousel viewport')
        return
      }
      this.thumbsEmbla = EmblaCarousel(thumbViewport, this.thumbOptions)
      this.setupNavigation()
      const initialIndex = this.mainEmbla.selectedScrollSnap()
      this.controlSlideVideos(initialIndex)
      this.updateOverviewButtonState(initialIndex)
      const initialThumbIndex = this.getCorrespondingThumbIndex(initialIndex)
      this.setSelectedThumbnail(initialThumbIndex)
    }

    setupNavigation () {
      const prevButtonNode = this.querySelector('.product-carousel__prev')
      const nextButtonNode = this.querySelector('.product-carousel__next')
      if (prevButtonNode) {
        const prevListener = this.handlePrevClick.bind(this)
        prevButtonNode.addEventListener('click', prevListener, false)
        this.navigationClickListeners.prev = { node: prevButtonNode, listener: prevListener }
      }
      if (nextButtonNode) {
        const nextListener = this.handleNextClick.bind(this)
        nextButtonNode.addEventListener('click', nextListener, false)
        this.navigationClickListeners.next = { node: nextButtonNode, listener: nextListener }
      }
      this.mainEmbla.on('select', () => {
        const mainIndex = this.mainEmbla.selectedScrollSnap()
        const thumbIndex = this.getCorrespondingThumbIndex(mainIndex)
        const DEBUG = window?.TB_DEBUG_CAROUSEL === true
        if (DEBUG) console.log({ tag: 'embla-select', mainIndex, thumbIndex })
        const thumbsNodes = this.thumbsEmbla.slideNodes()
        const totalSlides = this.mainEmbla.slideNodes().length
        const isLongformSlide = mainIndex === totalSlides - 1
        if (isLongformSlide) {
          thumbsNodes.forEach(t => t.classList.remove('product-carousel-thumbs__slide--selected'))
        }
        if (window.innerWidth < 1280 && !isLongformSlide) {
          this.thumbsEmbla.scrollTo(thumbIndex, true)
        }
        if (thumbIndex < thumbsNodes.length && !isLongformSlide) {
          this.setSelectedThumbnail(thumbIndex)
        }
        this.updateOverviewButtonState(mainIndex)
        this.trackSlideChange(mainIndex)
        this.controlSlideVideos(mainIndex)
      })
      this.handleKeydown = this.handleKeydown.bind(this)
      this.addEventListener('keydown', this.handleKeydown)
      if (!this.hasAttribute('tabindex')) {
        this.setAttribute('tabindex', '0')
      }
    }

    controlSlideVideos (activeIndex) {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      Object.values(this.hintTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.hintTimeouts = {}
      const slides = this.mainEmbla.slideNodes()
      slides.forEach((slide, index) => {
        const productGalleryVideo = slide.querySelector('product-gallery-video')
        const deferredMedia = slide.querySelector('deferred-media')
        const video = slide.querySelector('video')
        if (index === activeIndex && productGalleryVideo && productGalleryVideo.classList.contains('controls-hidden') === false) {
          const fsBtn = productGalleryVideo.querySelector('.fullscreen-btn')
          const hint = fsBtn ? fsBtn.querySelector('.fullscreen-hint') : null
          if (hint) {
            hint.classList.remove('opacity-100')
            hint.classList.add('opacity-0')
            this.hintTimeouts[`hint-show-${index}`] = setTimeout(() => {
              hint.classList.remove('opacity-0')
              hint.classList.add('opacity-100')
              this.hintTimeouts[`hint-hide-${index}`] = setTimeout(() => {
                hint.classList.remove('opacity-100')
                hint.classList.add('opacity-0')
              }, 6000)
            }, 2000)
          }
        }
        if (productGalleryVideo && typeof productGalleryVideo.loadVideo === 'function') {
          if (index === activeIndex) {
            if (productGalleryVideo.videoLoaded) {
              if (typeof productGalleryVideo.resumeForSlideChange === 'function') {
                productGalleryVideo.resumeForSlideChange()
              }
            } else if (!productGalleryVideo.isLoading) {
              productGalleryVideo.loadVideo()
            }
            const altText = productGalleryVideo.dataset.alt || ''
            const isLongformSlide = slide.classList.contains('product-carousel__slide--longform')
            if (altText.includes('[longformAuto]') || isLongformSlide) {
              const attemptPlay = () => {
                if (productGalleryVideo.videoLoaded && typeof productGalleryVideo.playVideo === 'function') {
                  productGalleryVideo.playVideo()
                } else {
                  setTimeout(attemptPlay, 200)
                }
              }
              attemptPlay()
            }
          } else {
            if (typeof productGalleryVideo.pauseForSlideChange === 'function') {
              productGalleryVideo.pauseForSlideChange()
            }
          }
        } else if (video) {
          const slideId = `video-${index}`
          if (index === activeIndex) {
            if (video.paused) {
              video.play()
            }
          } else {
            this.videoTimeouts[slideId] = setTimeout(() => {
              video.pause()
            }, 500)
          }
        }
        if (deferredMedia) {
          const slideId = `vimeo-${index}`
          if (index === activeIndex) {
            if (!deferredMedia.getAttribute('loaded')) {
              deferredMedia.loadContent(false)
            }
            this.videoTimeouts[slideId] = setTimeout(() => {
              const vimeoIframe = deferredMedia.querySelector('iframe.js-vimeo')
              if (vimeoIframe) {
                vimeoIframe.contentWindow.postMessage('{"method":"play"}', '*')
              }
            }, 100)
          } else {
            const vimeoIframe = deferredMedia.querySelector('iframe.js-vimeo')
            if (vimeoIframe) {
              vimeoIframe.contentWindow.postMessage('{"method":"pause"}', '*')
            }
          }
        }
      })
    }

    trackSlideChange (index) {
      const slide = this.mainEmbla.slideNodes()[index]
      if (!slide) return
      let eventName = 'product-carousel-slide'
      let mediaType = 'unknown'
      const productGalleryVideo = slide.querySelector('product-gallery-video')
      if (productGalleryVideo) {
        mediaType = 'video'
        const videoSrc = productGalleryVideo.dataset.source
        if (videoSrc) {
          const fileName = videoSrc.split('/').pop().split('?')[0].split('.')[0]
          eventName = `product-carousel-video-slide${index + 1}-${fileName}`
        }
      } else {
        const video = slide.querySelector('video')
        if (video) {
          mediaType = 'video'
          const videoSrc = video.querySelector('source')?.src || video.src
          if (videoSrc) {
            const fileName = videoSrc.split('/').pop().split('?')[0].split('.')[0]
            eventName = `product-carousel-video-slide${index + 1}-${fileName}`
          }
        } else {
          const img = slide.querySelector('img')
          if (img) {
            mediaType = 'image'
            const src = img.src
            const fileName = src.split('/').pop().split('?')[0].split('.')[0]
            eventName = `product-carousel-image-slide${index + 1}-${fileName}`
          }
        }
      }
      if (typeof gtag !== 'undefined') {
        gtag('event', eventName, {
          mediaType,
          slideIndex: index,
        })
      }
    }

    removeNavigationListeners () {
      if (this.navigationClickListeners.prev) {
        const { node, listener } = this.navigationClickListeners.prev
        node.removeEventListener('click', listener)
      }
      if (this.navigationClickListeners.next) {
        const { node, listener } = this.navigationClickListeners.next
        node.removeEventListener('click', listener)
      }
      this.navigationClickListeners = {}
    }

    setupThumbnailClick () {
      // Handle thumbnail clicks for the active carousel
      const thumbsNodes = this.thumbsEmbla.slideNodes()
      thumbsNodes.forEach((thumb, index) => {
        const listener = () => {
          this.setSelectedThumbnail(index)
          const mainIndex = this.getCorrespondingMainIndex(index)
          this.mainEmbla.scrollTo(mainIndex, true)
          this.thumbsEmbla.scrollTo(index, true)
          this.updateOverviewButtonState(mainIndex)
        }
        thumb.addEventListener('click', listener)
        this.thumbnailClickListeners.push({ thumb, listener })
      })
      const overviewButtons = this.querySelectorAll('.product-carousel-thumbs__slide--overview')
      overviewButtons.forEach(overviewButton => {
        const overviewListener = () => {
          const totalSlides = this.mainEmbla.slideNodes().length
          const lastSlideIndex = totalSlides - 1
          if (totalSlides > 0) {
            const thumbsNodes = this.thumbsEmbla.slideNodes()
            thumbsNodes.forEach(thumb => thumb.classList.remove('product-carousel-thumbs__slide--selected'))
            if (window.innerWidth < 1280) {
              this.thumbsEmbla.scrollTo(0, true)
            }
            this.mainEmbla.scrollTo(lastSlideIndex, true)
          }
        }
        overviewButton.addEventListener('click', overviewListener)
        this.thumbnailClickListeners.push({ thumb: overviewButton, listener: overviewListener })
      })
    }

    handleFirstTimeLoad () {
      setTimeout(() => {
        this.reinitializeCarousel()
      }, 500)
    }

    removeThumbnailClickListeners () {
      this.thumbnailClickListeners.forEach(({ thumb, listener }) => {
        thumb.removeEventListener('click', listener)
      })
      this.thumbnailClickListeners = []
    }

    setSelectedThumbnail (index) {
      const thumbsNodes = this.thumbsEmbla.slideNodes()

      // Clear all thumbnail carousel slides (not overview buttons)
      thumbsNodes.forEach(t => t.classList.remove('product-carousel-thumbs__slide--selected'))
      const overviewButtons = this.querySelectorAll('.product-carousel-thumbs__slide--overview')
      overviewButtons.forEach(btn => btn.classList.remove('product-carousel-thumbs__slide--selected'))
      if (thumbsNodes[index]) {
        thumbsNodes[index].classList.add('product-carousel-thumbs__slide--selected')
      }
    }

    handlePrevClick () {
      this.mainEmbla.scrollPrev(true)
      const mainIndex = this.mainEmbla.selectedScrollSnap()
      const thumbIndex = this.getCorrespondingThumbIndex(mainIndex)
      const totalSlides = this.mainEmbla.slideNodes().length
      const isLongformSlide = mainIndex === totalSlides - 1
      if (window.innerWidth < 1280 && !isLongformSlide) {
        this.thumbsEmbla.scrollTo(thumbIndex, true)
      }
      if (!isLongformSlide) {
        this.setSelectedThumbnail(thumbIndex)
      }
      this.updateOverviewButtonState(mainIndex)
    }

    handleNextClick () {
      this.mainEmbla.scrollNext(true)
      const mainIndex = this.mainEmbla.selectedScrollSnap()
      const thumbIndex = this.getCorrespondingThumbIndex(mainIndex)
      const totalSlides = this.mainEmbla.slideNodes().length
      const isLongformSlide = mainIndex === totalSlides - 1
      if (window.innerWidth < 1280 && !isLongformSlide) {
        this.thumbsEmbla.scrollTo(thumbIndex, true)
      }
      if (!isLongformSlide) {
        this.setSelectedThumbnail(thumbIndex)
      }
      this.updateOverviewButtonState(mainIndex)
    }

    handleKeydown (event) {
      if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
        event.preventDefault()
        if (event.key === 'ArrowRight') {
          this.handleNextClick()
        } else if (event.key === 'ArrowLeft') {
          this.handlePrevClick()
        }
      }
    }

    getCorrespondingThumbIndex (mainIndex) {
      if (this.hasLongformVideo && window.innerWidth >= 1280) {
        const totalMainSlides = this.mainEmbla.slideNodes().length
        const isLongformSlide = mainIndex === totalMainSlides - 1
        if (isLongformSlide) {
          return 0
        } else {
          return mainIndex + 1
        }
      }
      return mainIndex
    }

    getCorrespondingMainIndex (thumbIndex) {
      if (this.hasLongformVideo && window.innerWidth >= 1280) {
        if (thumbIndex === 0) {
          const totalMainSlides = this.mainEmbla.slideNodes().length
          return totalMainSlides - 1
        } else {
          return thumbIndex - 1
        }
      }
      return thumbIndex
    }

    getInitialThumbIndex () {
      return 0
    }

    getInitialMainIndex () {
      return 0
    }

    setupOverviewButtonSizing () {
      if (!this.hasLongformVideo || window.innerWidth >= 1280) return
      requestAnimationFrame(() => {
        setTimeout(() => {
          this.syncOverviewButtonSize()
          setTimeout(() => {
            this.syncOverviewButtonSize()
          }, 200)
        }, 100)
      })
    }

    async waitForThumbStableSize (maxAttempts = 20) {
      const mobileContainer = this.querySelector('.product-carousel-thumbs__container-mobile')
      if (!mobileContainer) return null
      let lastWidth = 0
      let lastHeight = 0
      for (let attempt = 0; attempt < maxAttempts; attempt++) {
        const firstSlide = mobileContainer.querySelector('.product-carousel-thumbs__slide')
        const firstSlideButton = firstSlide?.querySelector('.product-carousel-thumbs__slide-button')
        const targetEl = firstSlideButton || firstSlide
        if (targetEl) {
          const rect = targetEl.getBoundingClientRect()
          const width = Math.round(rect.width * 100) / 100
          const height = Math.round(rect.height * 100) / 100
          if (width > 0 && height > 0) {
            if (Math.abs(width - lastWidth) < 0.5 && Math.abs(height - lastHeight) < 0.5 && attempt > 0) {
              console.log({ tag: 'thumb-stable-size', slide: { width, height } })
              return { width, height }
            }
            lastWidth = width
            lastHeight = height
          }
        }
        // eslint-disable-next-line no-await-in-loop
        await new Promise(resolve => requestAnimationFrame(resolve))
      }
      return { width: lastWidth, height: lastHeight }
    }

    syncOverviewButtonSize () {
      if (!this.hasLongformVideo || window.innerWidth >= 1280) return
      const overviewButton = this.querySelector('.product-carousel-thumbs__slide--overview')
      const mobileContainer = this.querySelector('.product-carousel-thumbs__container-mobile')
      if (overviewButton && mobileContainer) {
        const firstSlide = mobileContainer.querySelector('.product-carousel-thumbs__slide')
        const firstSlideButton = firstSlide?.querySelector('.product-carousel-thumbs__slide-button')
        if (firstSlide && firstSlideButton) {
          const buttonRect = firstSlideButton.getBoundingClientRect()
          if (buttonRect.width > 0 && buttonRect.height > 0) {
            overviewButton.style.setProperty('height', `${buttonRect.height}px`, 'important')
            overviewButton.style.removeProperty('width')
            overviewButton.style.removeProperty('min-width')
            overviewButton.style.removeProperty('max-width')
            overviewButton.style.removeProperty('flex-basis')
            overviewButton.style.setProperty('flex-shrink', '0', 'important')
            overviewButton.style.setProperty('flex-grow', '0', 'important')
          }
        }
      }
    }

    updateOverviewButtonState (mainIndex) {
      if (!this.hasLongformVideo) return
      const totalSlides = this.mainEmbla.slideNodes().length
      const isOnLongformSlide = mainIndex === totalSlides - 1
      const overviewButtons = this.querySelectorAll('.product-carousel-thumbs__slide--overview')
      overviewButtons.forEach(button => {
        if (isOnLongformSlide) {
          button.classList.add('product-carousel-thumbs__slide--selected')
        } else {
          button.classList.remove('product-carousel-thumbs__slide--selected')
        }
      })
    }

    handleResize () {
      clearTimeout(this.resizeTimeout)
      this.resizeTimeout = setTimeout(() => {
        const currentViewport = window.innerWidth >= 1280 ? 'desktop' : 'mobile'
        const viewportChanged = this.lastViewport && this.lastViewport !== currentViewport
        if (viewportChanged && this.hasLongformVideo) {
          console.log('Viewport changed with longform video - reinitializing carousel')
          this.reinitializeCarousel()
        } else {
          this.syncOverviewButtonSize()
        }
        this.lastViewport = currentViewport
      }, 150)
    }

    async onVariantChange (event) {
      const { variant } = event.detail
      const variantId = String(variant.id)
      const variantImages = this.variantImages[variantId] || []
      this.featuredMedia.forEach((featuredMedia) => {
        featuredMedia.classList.toggle('tb-hidden', featuredMedia.dataset.variantId !== variantId)
      })
      this.removeAdditionalImages()
      await this.addAdditionalImages(variantId, variantImages).then(() => {
        this.reinitializeCarousel()
      })
    }

    removeAdditionalImages () {
      const additionalSlides = this.querySelectorAll('.product-carousel__slide--additional-image')
      additionalSlides.forEach(slide => slide.remove())
      const additionalThumbs = this.querySelectorAll('.product-carousel-thumbs__slide--additional-image')
      additionalThumbs.forEach(thumb => thumb.remove())
    }

    async addAdditionalImages (variantId, images) {
      const carouselContainer = this.querySelector('.product-carousel__container')
      const thumbsContainer = this.querySelector('.product-carousel-thumbs__container')
      const featuredImageSlide = carouselContainer.querySelector('.product-carousel__slide--featured')
      const featuredThumbSlide = thumbsContainer.querySelector('.product-carousel-thumbs__slide--featured')
      const imageIndexOffset = 1
      images.forEach((image, index) => {
        const imageIndex = imageIndexOffset + index
        const slide = document.createElement('div')
        slide.classList.add('product-carousel__slide', 'product-carousel__slide--additional-image')
        slide.dataset.variantId = variantId
        slide.dataset.imageIndex = imageIndex
        slide.innerHTML = `<img src="${image}" class="w-full max-w-full rounded-sm bg-oat object-cover aspect-[4/3]" alt="Additional image">`
        if (featuredImageSlide) {
          featuredImageSlide.insertAdjacentElement('afterend', slide)
        }
        const thumbSlide = document.createElement('div')
        thumbSlide.classList.add('product-carousel-thumbs__slide', 'product-carousel-thumbs__slide--additional-image')
        thumbSlide.dataset.variantId = variantId
        thumbSlide.dataset.imageIndex = imageIndex
        thumbSlide.innerHTML = `<button class="product-carousel-thumbs__slide-button rounded overflow-hidden block w-full h-full cursor-pointer" type="button" aria-label="View image ${imageIndex + 1}">
                                  <img src="${image}" class="w-full max-w-full rounded-sm bg-oat object-cover aspect-[4/3]" alt="Additional thumbnail">
                                </button>`
        if (featuredThumbSlide) {
          featuredThumbSlide.insertAdjacentElement('afterend', thumbSlide)
        }
      })
    }

    async reinitializeCarousel () {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.mainEmbla.destroy()
      this.thumbsEmbla.destroy()
      this.removeThumbnailClickListeners()
      this.removeNavigationListeners()
      this.mainEmbla = null
      this.thumbsEmbla = null

      await this.setupCarousels().then(() => {
        this.setupThumbnailClick()
        this.setupOverviewButtonSizing()
        this.mainEmbla.scrollTo(0, true)
        this.thumbsEmbla.scrollTo(0, true)
        const thumbIndex = this.getCorrespondingThumbIndex(0)
        this.setSelectedThumbnail(thumbIndex)
        this.updateOverviewButtonState(0)
        this.syncOverviewButtonSize()
      })
    }

    disconnectedCallback () {
      Object.values(this.videoTimeouts).forEach(timeout => clearTimeout(timeout))
      this.videoTimeouts = {}
      this.removeThumbnailClickListeners()
      this.removeNavigationListeners()
      window.removeEventListener('resize', this.handleResize)
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout)
      }
    }

    set loading (value) {
      this.setAttribute('loading', value)
    }

    get loading () {
      return this.getAttribute('loading') === 'true'
    }

    get initialVariantId () {
      return this.dataset.initialVariantId
    }

    get featuredMedia () {
      return this.querySelectorAll('.product-media__variant-featured')
    }

    get variantImages () {
      return this.dataset.variantImages ? JSON.parse(this.dataset.variantImages) : {}
    }

    get hasLongformVideo () {
      return this.dataset.hasLongformVideo === 'true'
    }

    get longformVideoId () {
      return this.dataset.longformVideoId
    }

    get allLongformVideos () {
      return this.dataset.allLongformVideos ? this.dataset.allLongformVideos.split(',') : []
    }
  }
  customElements.define('product-media', ProductMedia)
}
/* eslint-enable  */
